import {Component, EventEmitter, inject, Input, OnChanges, Output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {GenericFormComponent} from '../../../../shared/components/generic-form/generic-form.component';
import {GameSession} from '../../../../shared/model/game-session';
import {pastDateValidator} from '../../../../shared/validators/date.validators';

export interface CreateGameSessionPayload {
  session: Partial<GameSession>;
  override: boolean;
}

@Component({
  selector: 'app-game-session-form',
  standalone: true,
  imports: [ReactiveFormsModule, GenericFormComponent],
  templateUrl: './game-session-form.component.html',
  styleUrl: './game-session-form.component.css'
})
export class GameSessionFormComponent implements OnChanges {
  private fb: FormBuilder = inject(FormBuilder);

  @Input({required: true}) tableId!: number;
  @Input() title: string = 'Aggiungi sessione';
  @Input() submitLabel: string = 'Crea sessione';
  @Input() showOverride: boolean = false;
  @Input() requireOverride: boolean = false;
  @Input() sessionToEdit?: GameSession;
  @Input() defaultStartDate?: string;
  @Input() eventStartDate?: string;
  @Input() eventEndDate?: string;
  @Output() formSubmit = new EventEmitter<CreateGameSessionPayload>();
  @Output() cancel = new EventEmitter<void>();

  protected sessionForm: FormGroup = this.fb.group(
    {
      startTimeLocal: ['', [Validators.required, pastDateValidator()]],
      endTimeLocal: [''],
      maxPlayers: [''],
      override: [false]
    },
    {
      validators: [
        this.endTimeAfterStartValidator.bind(this),
        this.eventDateRangeValidator.bind(this)
      ]
    }
  );

  get minDateTime(): string | undefined {
    return this.eventStartDate ? `${this.eventStartDate}T00:00` : undefined;
  }

  get maxDateTime(): string | undefined {
    return (this.eventEndDate || this.eventStartDate) ? `${this.eventEndDate || this.eventStartDate}T23:59` : undefined;
  }

  ngOnChanges(): void {
    const overrideControl = this.sessionForm.get('override');
    if (!overrideControl) {
      return;
    }

    if (this.showOverride && this.requireOverride) {
      overrideControl.setValidators([Validators.requiredTrue]);
    } else {
      overrideControl.clearValidators();
    }
    overrideControl.updateValueAndValidity({emitEvent: false});

    // Pre-popola il form se siamo in modalità modifica o se c'è una data di default
    if (this.sessionToEdit) {
      this.populateFormForEdit();
    } else if (this.defaultStartDate) {
      const defaultDate = new Date(this.defaultStartDate);
      if (!isNaN(defaultDate.getTime())) {
        this.sessionForm.patchValue({
          startTimeLocal: this.formatLocalDateTime(defaultDate)
        });
      }
    }
  }

  private formatLocalDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private populateFormForEdit(): void {
    if (!this.sessionToEdit) {
      return;
    }

    const startDate = this.sessionToEdit.startTime ? new Date(this.sessionToEdit.startTime) : null;
    const endDate = this.sessionToEdit.endTime ? new Date(this.sessionToEdit.endTime) : null;

    this.sessionForm.patchValue({
      startTimeLocal: startDate ? this.formatLocalDateTime(startDate) : '',
      endTimeLocal: endDate ? this.formatLocalDateTime(endDate) : '',
      maxPlayers: this.sessionToEdit.maxPlayers ?? ''
    });
  }

  onSubmit(): void {
    if (!this.sessionForm.valid) {
      this.sessionForm.markAllAsTouched();
      return;
    }

    const startTime = this.toEpochMillis(this.sessionForm.value.startTimeLocal);
    const endTime = this.toEpochMillis(this.sessionForm.value.endTimeLocal);

    if (startTime == null) {
      this.sessionForm.get('startTimeLocal')?.setErrors({required: true});
      return;
    }

    const maxPlayers = this.sessionForm.value.maxPlayers
      ? Number(this.sessionForm.value.maxPlayers)
      : null;

    const payload: CreateGameSessionPayload = {
      session: {
        id: this.sessionToEdit?.id,
        gameTableId: this.tableId,
        startTime,
        endTime: endTime ?? undefined,
        maxPlayers
      },
      override: this.showOverride ? Boolean(this.sessionForm.value.override) : false
    };

    this.formSubmit.emit(payload);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  private endTimeAfterStartValidator(control: AbstractControl): ValidationErrors | null {
    const start = this.toEpochMillis(control.get('startTimeLocal')?.value);
    const end = this.toEpochMillis(control.get('endTimeLocal')?.value);

    if (start == null || end == null) {
      return null;
    }

    return end >= start ? null : {endBeforeStart: true};
  }

  private eventDateRangeValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.eventStartDate) {
      return null;
    }

    const startStr = control.get('startTimeLocal')?.value;
    const endStr = control.get('endTimeLocal')?.value;

    const eventStart = new Date(`${this.eventStartDate}T00:00:00`).getTime();
    const eventEnd = new Date(`${this.eventEndDate || this.eventStartDate}T23:59:59.999`).getTime();

    if (startStr) {
      const start = new Date(startStr).getTime();
      if (start < eventStart || start > eventEnd) return {outsideEventDates: true};
    }

    if (endStr) {
      const end = new Date(endStr).getTime();
      if (end < eventStart || end > eventEnd) return {outsideEventDates: true};
    }

    return null;
  }

  private toEpochMillis(localDateTime: string | null | undefined): number | null {
    if (!localDateTime || !String(localDateTime).trim()) {
      return null;
    }

    const millis = Date.parse(localDateTime);
    return Number.isNaN(millis) ? null : millis;
  }
}
